import { routes } from "@constants/routes";
import { ConfirmModal, handleApiRequest } from "@cpj-ai/panel-npm-package";
import {
  AuthAccountJobPositionAssignmentStatusChoices,
  UpdateJobPositionAssignmentStatusMutation,
  UpdateJobPositionAssignmentStatusMutationVariables,
  useUpdateJobPositionAssignmentStatusMutation,
} from "@graphql/generated";
import { Button, useDisclosure } from "@heroui/react";
import { useCallback, useState } from "react"; // Add useState
import { useNavigate, useParams } from "react-router-dom";

const Actions = () => {
  const { id } = useParams();
  const [updateJobPositionAssignmentStatus] = useUpdateJobPositionAssignmentStatusMutation();
  const navigate = useNavigate();
  const {
    isOpen: isRejectConfirmModalOpen,
    onClose: onRejectConfirmModalClose,
    onOpen: onRejectConfirmModalOpen,
  } = useDisclosure();
  const {
    isOpen: isAcceptConfirmModalOpen,
    onClose: onAcceptConfirmModalClose,
    onOpen: onAcceptConfirmModalOpen,
  } = useDisclosure();

  // Add separate loading states for each button
  const [isAcceptLoading, setIsAcceptLoading] = useState(false);
  const [isRejectLoading, setIsRejectLoading] = useState(false);

  const handleAccept = useCallback(async () => {
    if (!id) return;

    setIsAcceptLoading(true);
    try {
      await handleApiRequest<
        UpdateJobPositionAssignmentStatusMutation,
        UpdateJobPositionAssignmentStatusMutationVariables
      >({
        apiFunction: updateJobPositionAssignmentStatus,
        variables: {
          id,
          status: AuthAccountJobPositionAssignmentStatusChoices.NOT_REVIEWED,
        },
        componentName: "JobSuggestionActions",
        actionName: "updateJobPositionAssignmentStatus",
        successMessage: "Resume sent successfully",
        fallbackErrorMessage: "Failed to send resume. Please try again.",
        onSuccess: () => {
          navigate(routes.jobSuggestions.main + "?tab=accepted-jobs");
        },
      });
    } catch (error) {
      console.error("Accept error:", error);
    } finally {
      setIsAcceptLoading(false);
    }
  }, [id, updateJobPositionAssignmentStatus, navigate]);

  const handleReject = useCallback(async () => {
    if (!id) return;

    setIsRejectLoading(true);
    try {
      await handleApiRequest<
        UpdateJobPositionAssignmentStatusMutation,
        UpdateJobPositionAssignmentStatusMutationVariables
      >({
        apiFunction: updateJobPositionAssignmentStatus,
        variables: {
          id,
          status: AuthAccountJobPositionAssignmentStatusChoices.REJECTED_BY_JOBSEEKER,
        },
        componentName: "JobSuggestionActions",
        actionName: "updateJobPositionAssignmentStatus",
        successMessage: "Job suggestion rejected successfully",
        fallbackErrorMessage: "Failed to reject job suggestion. Please try again.",
        onSuccess: () => {
          navigate(routes.jobSuggestions.main + "?tab=rejected-jobs");
        },
      });
    } catch (error) {
      console.error("Reject error:", error);
    } finally {
      setIsRejectLoading(false);
    }
  }, [id, updateJobPositionAssignmentStatus, navigate]);

  return (
    <div className="w-full flex flex-col gap-2">
      <ConfirmModal
        isOpen={isRejectConfirmModalOpen}
        onClose={onRejectConfirmModalClose}
        messageClassName="text-center text-base"
        message="Are you sure you want to reject this job post?"
        confirmLabel="Yes"
        onConfirm={handleReject}
      />

      <ConfirmModal
        isOpen={isAcceptConfirmModalOpen}
        onClose={onAcceptConfirmModalClose}
        messageClassName="text-center text-base"
        message="Are you sure you want to accept this job post?"
        confirmLabel="Yes"
        onConfirm={handleAccept}
      />

      <Button
        color="success"
        onPress={onAcceptConfirmModalOpen}
        isLoading={isAcceptLoading}
        disabled={isAcceptLoading || isRejectLoading}
      >
        {isAcceptLoading ? "Accepting..." : "Accept and send resume"}
      </Button>
      <Button
        color="primary"
        variant="bordered"
        onPress={onRejectConfirmModalOpen}
        isLoading={isRejectLoading}
        disabled={isAcceptLoading || isRejectLoading}
      >
        {isRejectLoading ? "Rejecting..." : "Reject send resume"}
      </Button>
    </div>
  );
};

export default Actions;
